import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { authAPI } from '../../api';
import { formatApiError } from '../../lib/apiError';


export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    try {
      const response = await authAPI.requestPasswordReset(email);
      setMessage(response.data.message);
    } catch (error) {
      setMessage(formatApiError(error, 'Unable to process the request. Please try again later.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="pw-container max-w-md py-16" aria-labelledby="forgot-password-title">
      <h1 id="forgot-password-title" className="text-3xl font-semibold">Reset your password</h1>
      <p className="mt-3 text-sm text-[#6f6a63]">Enter the email associated with your Petals West account.</p>
      <form onSubmit={submit} className="mt-8 space-y-4">
        <div>
          <label htmlFor="recovery-email" className="mb-1 block text-sm font-medium">Email</label>
          <input id="recovery-email" type="email" autoComplete="email" required value={email}
            onChange={(event) => setEmail(event.target.value)}
            className="w-full rounded-lg border border-[#e7e3dd] px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#2f6b4f]" />
        </div>
        <button type="submit" disabled={submitting}
          className="w-full rounded-lg bg-[#2f6b4f] px-4 py-3 font-medium text-white disabled:opacity-50">
          {submitting ? 'Sending…' : 'Send reset instructions'}
        </button>
      </form>
      {message && <p role="status" aria-live="polite" className="mt-5 text-sm">{message}</p>}
      <Link to="/login" className="mt-6 inline-block text-sm text-[#2f6b4f] underline">Back to sign in</Link>
    </main>
  );
}


export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [password, setPassword] = useState('');
  const [message, setMessage] = useState('');
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    try {
      const response = await authAPI.confirmPasswordReset(token, password);
      setMessage(response.data.message);
      setDone(true);
    } catch (error) {
      setMessage(formatApiError(error, 'Unable to reset the password.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="pw-container max-w-md py-16" aria-labelledby="reset-password-title">
      <h1 id="reset-password-title" className="text-3xl font-semibold">Choose a new password</h1>
      {!token ? <p role="alert" className="mt-5 text-sm text-red-700">This reset link is incomplete.</p> : (
        <form onSubmit={submit} className="mt-8 space-y-4">
          <div>
            <label htmlFor="new-password" className="mb-1 block text-sm font-medium">New password</label>
            <input id="new-password" type="password" autoComplete="new-password" required minLength={12} maxLength={72}
              value={password} onChange={(event) => setPassword(event.target.value)} aria-describedby="new-password-help"
              className="w-full rounded-lg border border-[#e7e3dd] px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#2f6b4f]" />
            <p id="new-password-help" className="mt-1 text-xs text-[#6f6a63]">Use at least 12 characters.</p>
          </div>
          <button type="submit" disabled={submitting || done}
            className="w-full rounded-lg bg-[#2f6b4f] px-4 py-3 font-medium text-white disabled:opacity-50">
            {submitting ? 'Updating…' : 'Update password'}
          </button>
        </form>
      )}
      {message && <p role={done ? 'status' : 'alert'} aria-live="polite" className="mt-5 text-sm">{message}</p>}
      {done && <Link to="/login" className="mt-6 inline-block text-sm text-[#2f6b4f] underline">Sign in</Link>}
    </main>
  );
}
